import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Calculator, History, Save, CheckCircle, TrendingUp, Activity, Percent, MapPin, ShieldAlert, Zap, Clock, Plus, X, Trash2, Package, Smartphone, UtensilsCrossed, Snowflake, ShieldCheck, Gem, Tag, ChevronDown, Pencil } from 'lucide-react';
import { MapContainer, TileLayer, Circle, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { StatsCard } from '../components/StatsCard';
import { DataTable } from '../components/DataTable';
const mockHistory = [{
  id: 'CHG-004',
  date: 'Today, 09:45 AM',
  admin: 'Admin User',
  changes: 'Updated Service Fee from 2.5% to 3%',
  status: 'active'
}, {
  id: 'CHG-003',
  date: 'Oct 12, 2023',
  admin: 'System',
  changes: 'Added FCT Dev Levy (₦30)',
  status: 'historical'
}, {
  id: 'CHG-002',
  date: 'Sep 05, 2023',
  admin: 'Manager',
  changes: 'Increased Distance Rate from ₦120 to ₦150',
  status: 'historical'
}, {
  id: 'CHG-001',
  date: 'Jan 01, 2023',
  admin: 'Super Admin',
  changes: 'Initial Pricing Setup',
  status: 'historical'
}];
// Simple Toggle Switch Component
const ToggleSwitch = ({
  enabled,
  onChange
}: {
  enabled: boolean;
  onChange: () => void;
}) => <button onClick={onChange} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-famo-gold/50 focus:ring-offset-1 ${enabled ? 'bg-famo-gold' : 'bg-slate-300'}`} data-id="element-737">
    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-4' : 'translate-x-1'}`} data-id="element-738" />
  </button>;
export function PricingConfig() {
  // Pricing Parameters State
  const [startingFee, setStartingFee] = useState<number>(650);
  const [distanceRate, setDistanceRate] = useState<number>(150);
  const [serviceFeePct, setServiceFeePct] = useState<number>(3);
  const [fctDevLevy, setFctDevLevy] = useState<number>(30);
  // Surge Pricing State
  const [surgeEnabled, setSurgeEnabled] = useState<boolean>(false);
  const [locationSurges, setLocationSurges] = useState([{
    id: 1,
    name: 'Victoria Island',
    percentage: 25,
    enabled: true,
    lat: 6.4281,
    lng: 3.4219,
    radius: 2000
  }, {
    id: 2,
    name: 'Lekki Phase 1',
    percentage: 20,
    enabled: true,
    lat: 6.4478,
    lng: 3.4723,
    radius: 1800
  }, {
    id: 3,
    name: 'Ikeja GRA',
    percentage: 15,
    enabled: false,
    lat: 6.5833,
    lng: 3.35,
    radius: 2500
  }, {
    id: 4,
    name: 'Airport Area',
    percentage: 30,
    enabled: true,
    lat: 6.5774,
    lng: 3.3212,
    radius: 3000
  }]);
  const [peakHourSurges, setPeakHourSurges] = useState([{
    id: 1,
    startTime: '07:00',
    endTime: '09:00',
    label: 'Morning Rush',
    percentage: 20,
    enabled: true
  }, {
    id: 2,
    startTime: '12:00',
    endTime: '14:00',
    label: 'Lunch Hour',
    percentage: 10,
    enabled: false
  }, {
    id: 3,
    startTime: '17:00',
    endTime: '20:00',
    label: 'Evening Rush',
    percentage: 25,
    enabled: true
  }, {
    id: 4,
    startTime: '22:00',
    endTime: '02:00',
    label: 'Late Night',
    percentage: 15,
    enabled: true
  }]);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: '',
    percentage: 0,
    lat: 6.5244,
    lng: 3.3792
  });
  const [showAddPeakHour, setShowAddPeakHour] = useState(false);
  const [newPeakHour, setNewPeakHour] = useState({
    startTime: '',
    endTime: '',
    label: '',
    percentage: 0
  });
  // Editing Surge State
  const [editingLocationId, setEditingLocationId] = useState<number | null>(null);
  const [editLocation, setEditLocation] = useState({
    name: '',
    percentage: 0,
    lat: 0,
    lng: 0
  });
  const [editingPeakHourId, setEditingPeakHourId] = useState<number | null>(null);
  const [editPeakHour, setEditPeakHour] = useState({
    startTime: '',
    endTime: '',
    label: '',
    percentage: 0
  });
  // Category Charges State
  const [categoryCharges, setCategoryCharges] = useState([{
    id: 1,
    name: 'Electronics',
    percentage: 10,
    enabled: true,
    description: 'Phones, laptops, tablets, gadgets'
  }, {
    id: 2,
    name: 'Fragile Items',
    percentage: 15,
    enabled: true,
    description: 'Glass, ceramics, artwork'
  }, {
    id: 3,
    name: 'Food Items',
    percentage: 5,
    enabled: true,
    description: 'Cooked meals, snacks, beverages'
  }, {
    id: 4,
    name: 'Perishable',
    percentage: 12,
    enabled: true,
    description: 'Fresh produce, dairy, frozen goods'
  }, {
    id: 5,
    name: 'Documents',
    percentage: 0,
    enabled: true,
    description: 'Letters, contracts, certificates'
  }, {
    id: 6,
    name: 'Luxury / High Value',
    percentage: 20,
    enabled: false,
    description: 'Jewelry, designer items, valuables'
  }]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    percentage: 0,
    description: ''
  });
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [editCategory, setEditCategory] = useState({
    name: '',
    percentage: 0,
    description: ''
  });
  // Calculator State
  const [sampleDistance, setSampleDistance] = useState<number>(13);
  const [calculatorSurgeEnabled, setCalculatorSurgeEnabled] = useState<boolean>(false);
  const [calculatorCategory, setCalculatorCategory] = useState<string>('none');
  // UI State
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);
  // Check active peak hour
  const getActivePeakHour = () => {
    if (!surgeEnabled) return null;
    const currentHours = currentTime.getHours();
    const currentMinutes = currentTime.getMinutes();
    const currentTotalMinutes = currentHours * 60 + currentMinutes;
    for (const peak of peakHourSurges) {
      if (!peak.enabled) continue;
      const [startH, startM] = peak.startTime.split(':').map(Number);
      const [endH, endM] = peak.endTime.split(':').map(Number);
      const startTotalMinutes = startH * 60 + startM;
      let endTotalMinutes = endH * 60 + endM;
      // Handle overnight shifts (e.g., 22:00 to 02:00)
      if (endTotalMinutes < startTotalMinutes) {
        if (currentTotalMinutes >= startTotalMinutes || currentTotalMinutes <= endTotalMinutes) {
          return peak;
        }
      } else {
        if (currentTotalMinutes >= startTotalMinutes && currentTotalMinutes <= endTotalMinutes) {
          return peak;
        }
      }
    }
    return null;
  };
  const activePeak = getActivePeakHour();
  // Calculations
  const distanceCost = distanceRate * sampleDistance;
  const subtotal = startingFee + distanceCost;
  // Surge calculation
  let activeSurgePct = 0;
  if (calculatorSurgeEnabled && surgeEnabled) {
    const maxLocationSurge = Math.max(0, ...locationSurges.filter(l => l.enabled).map(l => l.percentage));
    const maxPeakSurge = Math.max(0, ...peakHourSurges.filter(p => p.enabled).map(p => p.percentage));
    activeSurgePct = Math.max(maxLocationSurge, maxPeakSurge);
  }
  const surgeAmount = subtotal * activeSurgePct / 100;
  // Category charge calculation
  const selectedCategory = categoryCharges.find(c => c.name === calculatorCategory && c.enabled);
  const categoryPct = selectedCategory ? selectedCategory.percentage : 0;
  const categoryAmount = subtotal * categoryPct / 100;
  const serviceFeeAmount = (subtotal + surgeAmount + categoryAmount) * serviceFeePct / 100;
  const totalFare = subtotal + surgeAmount + categoryAmount + serviceFeeAmount + fctDevLevy;
  const handleSave = () => {
    setIsSaving(true);
    setShowSuccess(false);
    setTimeout(() => {
      setIsSaving(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }, 1000);
  };
  // Surge Handlers
  const toggleLocationSurge = (id: number) => {
    setLocationSurges(prev => prev.map(loc => loc.id === id ? {
      ...loc,
      enabled: !loc.enabled
    } : loc));
  };
  const deleteLocationSurge = (id: number) => {
    setLocationSurges(prev => prev.filter(loc => loc.id !== id));
    if (editingLocationId === id) setEditingLocationId(null);
  };
  const startEditLocation = (loc: {
    id: number;
    name: string;
    percentage: number;
    lat: number;
    lng: number;
  }) => {
    setEditingLocationId(loc.id);
    setEditLocation({
      name: loc.name,
      percentage: loc.percentage,
      lat: loc.lat,
      lng: loc.lng
    });
  };
  const handleEditLocation = () => {
    if (editingLocationId !== null && editLocation.name) {
      setLocationSurges(prev => prev.map(loc => loc.id === editingLocationId ? {
        ...loc,
        name: editLocation.name,
        percentage: editLocation.percentage,
        lat: editLocation.lat,
        lng: editLocation.lng
      } : loc));
      setEditingLocationId(null);
    }
  };
  const cancelEditLocation = () => {
    setEditingLocationId(null);
  };
  const handleAddLocation = () => {
    if (newLocation.name && newLocation.percentage > 0 && newLocation.lat && newLocation.lng) {
      setLocationSurges([...locationSurges, {
        id: Date.now(),
        ...newLocation,
        radius: 2000,
        enabled: true
      }]);
      setNewLocation({
        name: '',
        percentage: 0,
        lat: 6.5244,
        lng: 3.3792
      });
      setShowAddLocation(false);
    }
  };
  const togglePeakHourSurge = (id: number) => {
    setPeakHourSurges(prev => prev.map(peak => peak.id === id ? {
      ...peak,
      enabled: !peak.enabled
    } : peak));
  };
  const deletePeakHourSurge = (id: number) => {
    setPeakHourSurges(prev => prev.filter(peak => peak.id !== id));
    if (editingPeakHourId === id) setEditingPeakHourId(null);
  };
  const startEditPeakHour = (peak: {
    id: number;
    startTime: string;
    endTime: string;
    label: string;
    percentage: number;
  }) => {
    setEditingPeakHourId(peak.id);
    setEditPeakHour({
      startTime: peak.startTime,
      endTime: peak.endTime,
      label: peak.label,
      percentage: peak.percentage
    });
  };
  const handleEditPeakHour = () => {
    if (editingPeakHourId !== null && editPeakHour.label) {
      setPeakHourSurges(prev => prev.map(peak => peak.id === editingPeakHourId ? {
        ...peak,
        startTime: editPeakHour.startTime,
        endTime: editPeakHour.endTime,
        label: editPeakHour.label,
        percentage: editPeakHour.percentage
      } : peak));
      setEditingPeakHourId(null);
    }
  };
  const cancelEditPeakHour = () => {
    setEditingPeakHourId(null);
  };
  const handleAddPeakHour = () => {
    if (newPeakHour.startTime && newPeakHour.endTime && newPeakHour.label && newPeakHour.percentage > 0) {
      setPeakHourSurges([...peakHourSurges, {
        id: Date.now(),
        ...newPeakHour,
        enabled: true
      }]);
      setNewPeakHour({
        startTime: '',
        endTime: '',
        label: '',
        percentage: 0
      });
      setShowAddPeakHour(false);
    }
  };
  // Category Handlers
  const toggleCategory = (id: number) => {
    setCategoryCharges(prev => prev.map(cat => cat.id === id ? {
      ...cat,
      enabled: !cat.enabled
    } : cat));
  };
  const deleteCategory = (id: number) => {
    setCategoryCharges(prev => prev.filter(cat => cat.id !== id));
    if (selectedCategory && selectedCategory.id === id) setCalculatorCategory('none');
    if (editingCategoryId === id) setEditingCategoryId(null);
  };
  const startEditCategory = (cat: {
    id: number;
    name: string;
    percentage: number;
    description: string;
  }) => {
    setEditingCategoryId(cat.id);
    setEditCategory({
      name: cat.name,
      percentage: cat.percentage,
      description: cat.description
    });
  };
  const handleEditCategory = () => {
    if (editingCategoryId !== null && editCategory.name) {
      setCategoryCharges(prev => prev.map(cat => cat.id === editingCategoryId ? {
        ...cat,
        name: editCategory.name,
        percentage: editCategory.percentage,
        description: editCategory.description
      } : cat));
      setEditingCategoryId(null);
    }
  };
  const cancelEditCategory = () => {
    setEditingCategoryId(null);
  };
  const handleAddCategory = () => {
    if (newCategory.name && newCategory.percentage >= 0) {
      setCategoryCharges([...categoryCharges, {
        id: Date.now(),
        ...newCategory,
        enabled: true
      }]);
      setNewCategory({
        name: '',
        percentage: 0,
        description: ''
      });
      setShowAddCategory(false);
    }
  };
  const getCategoryIcon = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes('electronic') || n.includes('phone') || n.includes('gadget')) return <Smartphone size={16} data-id="element-739" />;
    if (n.includes('fragile') || n.includes('glass')) return <ShieldCheck size={16} data-id="element-740" />;
    if (n.includes('food') || n.includes('meal')) return <UtensilsCrossed size={16} data-id="element-741" />;
    if (n.includes('perish') || n.includes('frozen') || n.includes('fresh')) return <Snowflake size={16} data-id="element-742" />;
    if (n.includes('luxury') || n.includes('high value') || n.includes('jewel')) return <Gem size={16} data-id="element-743" />;
    if (n.includes('document') || n.includes('letter')) return <Tag size={16} data-id="element-744" />;
    return <Package size={16} data-id="element-745" />;
  };
  const historyColumns = [{
    header: 'Change ID',
    accessor: 'id',
    className: 'font-mono text-xs text-slate-500'
  }, {
    header: 'Date',
    accessor: 'date'
  }, {
    header: 'Changed By',
    accessor: 'admin',
    className: 'font-medium text-slate-900'
  }, {
    header: 'Description',
    accessor: 'changes'
  }, {
    header: 'Status',
    accessor: (row: any) => <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-746">
          {row.status.toUpperCase()}
        </span>
  }];
  return <div className="space-y-6" data-id="element-747">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-748">
        <StatsCard title="Current Base Rate" value={`₦${startingFee}`} trend={0} icon={DollarSign} color="gold" delay={0} data-id="element-749" />
        <StatsCard title="Avg Trip Cost" value="₦2,450" trend={5} icon={TrendingUp} color="blue" delay={0.1} data-id="element-750" />
        <StatsCard title="Service Fee Rate" value={`${serviceFeePct}%`} trend={0} icon={Percent} color="green" delay={0.2} data-id="element-751" />
        <StatsCard title="Total Charges Today" value="₦142,500" trend={12} icon={Activity} color="purple" delay={0.3} data-id="element-752" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-753">
        {/* Configuration Form */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col" data-id="element-754">
          <div className="flex items-center justify-between mb-6" data-id="element-755">
            <div data-id="element-756">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2" data-id="element-757">
                <DollarSign size={20} className="text-famo-gold" data-id="element-758" />
                Charge Configuration
              </h3>
              <p className="text-sm text-slate-500 mt-1" data-id="element-759">
                Update the core pricing parameters for all deliveries.
              </p>
            </div>
          </div>

          <div className="space-y-5 flex-1" data-id="element-760">
            <div data-id="element-761">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-762">
                Starting Fee
              </label>
              <p className="text-xs text-slate-500 mb-2" data-id="element-763">
                Flat base amount charged at the start of every trip.
              </p>
              <div className="relative" data-id="element-764">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium" data-id="element-765">
                  ₦
                </span>
                <input type="number" value={startingFee} onChange={e => setStartingFee(Number(e.target.value))} className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm pl-8 pr-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" data-id="element-766" />
              </div>
            </div>
            <div data-id="element-767">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-768">
                Distance Rate (per km)
              </label>
              <p className="text-xs text-slate-500 mb-2" data-id="element-769">
                Amount charged for every kilometer traveled.
              </p>
              <div className="relative" data-id="element-770">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium" data-id="element-771">
                  ₦
                </span>
                <input type="number" value={distanceRate} onChange={e => setDistanceRate(Number(e.target.value))} className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm pl-8 pr-12 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" data-id="element-772" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-773">
                  /km
                </span>
              </div>
            </div>
            <div data-id="element-774">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-775">
                Service Fee
              </label>
              <p className="text-xs text-slate-500 mb-2" data-id="element-776">
                Percentage charged on the total distance cost.
              </p>
              <div className="relative" data-id="element-777">
                <input type="number" value={serviceFeePct} onChange={e => setServiceFeePct(Number(e.target.value))} className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm pl-4 pr-8 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" data-id="element-778" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium" data-id="element-779">
                  %
                </span>
              </div>
            </div>
            <div data-id="element-780">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-781">
                FCT Dev Levy
              </label>
              <p className="text-xs text-slate-500 mb-2" data-id="element-782">
                Flat government charge applied to every trip.
              </p>
              <div className="relative" data-id="element-783">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium" data-id="element-784">
                  ₦
                </span>
                <input type="number" value={fctDevLevy} onChange={e => setFctDevLevy(Number(e.target.value))} className="w-full bg-slate-50 border border-slate-200 rounded-lg text-sm pl-8 pr-4 py-2 focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none" data-id="element-785" />
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between" data-id="element-786">
            <div className="flex items-center gap-2" data-id="element-787">
              {showSuccess && <motion.div initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} className="flex items-center gap-1.5 text-green-600 text-sm font-medium" data-id="element-788">
                  <CheckCircle size={16} data-id="element-789" />
                  <span data-id="element-790">Saved successfully</span>
                </motion.div>}
            </div>
            <button onClick={handleSave} disabled={isSaving} className="bg-famo-gold text-white hover:opacity-90 rounded-lg px-6 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-70" data-id="element-791">
              {isSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-792" /> : <Save size={16} data-id="element-793" />}
              Save Changes
            </button>
          </div>
        </motion.div>

        {/* Live Fare Calculator */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-famo-navy rounded-xl shadow-lg border border-slate-800 text-white flex flex-col overflow-hidden relative" data-id="element-794">
          <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-795" />

          <div className="p-6 border-b border-slate-800 relative z-10 flex justify-between items-start" data-id="element-796">
            <div data-id="element-797">
              <h3 className="text-lg font-bold flex items-center gap-2 text-famo-gold" data-id="element-798">
                <Calculator size={20} data-id="element-799" />
                Live Fare Preview
              </h3>
              <p className="text-sm text-slate-400 mt-1" data-id="element-800">
                Test how your current pricing configuration affects trip fares.
              </p>
            </div>
            {surgeEnabled && <div className="flex items-center gap-2 bg-slate-800/80 px-3 py-1.5 rounded-lg border border-slate-700" data-id="element-801">
                <span className="text-xs font-medium text-slate-300" data-id="element-802">
                  Test Surge
                </span>
                <ToggleSwitch enabled={calculatorSurgeEnabled} onChange={() => setCalculatorSurgeEnabled(!calculatorSurgeEnabled)} data-id="element-803" />
              </div>}
          </div>

          <div className="p-6 flex-1 relative z-10" data-id="element-804">
            {/* Distance Slider */}
            <div className="mb-5 bg-slate-800/50 p-4 rounded-lg border border-slate-700" data-id="element-805">
              <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2" data-id="element-806">
                <MapPin size={16} className="text-famo-gold" data-id="element-807" />
                Test Distance (Kilometers)
              </label>
              <div className="flex items-center gap-3" data-id="element-808">
                <input type="range" min="1" max="50" value={sampleDistance} onChange={e => setSampleDistance(Number(e.target.value))} className="flex-1 accent-famo-gold" data-id="element-809" />
                <div className="w-20 relative" data-id="element-810">
                  <input type="number" value={sampleDistance} onChange={e => setSampleDistance(Number(e.target.value))} className="w-full bg-slate-900 border border-slate-700 rounded-lg text-sm px-3 py-2 text-white focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none text-center" data-id="element-811" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none" data-id="element-812">
                    km
                  </span>
                </div>
              </div>
            </div>

            {/* Category Selector */}
            <div className="mb-6 bg-slate-800/50 p-4 rounded-lg border border-slate-700" data-id="element-813">
              <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2" data-id="element-814">
                <Package size={16} className="text-famo-gold" data-id="element-815" />
                Item Category
              </label>
              <div className="relative" data-id="element-816">
                <select value={calculatorCategory} onChange={e => setCalculatorCategory(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg text-sm px-3 py-2 text-white focus:ring-2 focus:ring-famo-gold/50 focus:border-famo-gold outline-none appearance-none cursor-pointer" data-id="element-817">
                  <option value="none" data-id="element-818">No category (standard)</option>
                  {categoryCharges.filter(c => c.enabled).map(cat => <option key={cat.id} value={cat.name} data-id="element-819">
                        {cat.name} (+{cat.percentage}%)
                      </option>)}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" data-id="element-820" />
              </div>
            </div>

            <div className="space-y-4" data-id="element-821">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-822">
                Fare Breakdown
              </h4>

              <div className="flex justify-between items-center text-sm" data-id="element-823">
                <span className="text-slate-300" data-id="element-824">Starting Fee</span>
                <span className="font-medium" data-id="element-825">₦{startingFee.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center text-sm" data-id="element-826">
                <span className="text-slate-300" data-id="element-827">
                  Distance Cost{' '}
                  <span className="text-slate-500 text-xs ml-1" data-id="element-828">
                    (₦{distanceRate} × {sampleDistance}km)
                  </span>
                </span>
                <span className="font-medium" data-id="element-829">₦{distanceCost.toFixed(2)}</span>
              </div>

              {calculatorSurgeEnabled && surgeEnabled && activeSurgePct > 0 && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="flex justify-between items-center text-sm text-amber-400 bg-amber-400/10 -mx-2 px-2 py-1 rounded" data-id="element-830">
                  <span className="flex items-center gap-1" data-id="element-831">
                    <Zap size={14} data-id="element-832" />
                    Surge Pricing{' '}
                    <span className="opacity-70 text-xs ml-1" data-id="element-833">
                      ({activeSurgePct}% of subtotal)
                    </span>
                  </span>
                  <span className="font-medium" data-id="element-834">
                    +₦{surgeAmount.toFixed(2)}
                  </span>
                </motion.div>}

              {categoryPct > 0 && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="flex justify-between items-center text-sm text-blue-400 bg-blue-400/10 -mx-2 px-2 py-1 rounded" data-id="element-835">
                  <span className="flex items-center gap-1" data-id="element-836">
                    <Package size={14} data-id="element-837" />
                    {calculatorCategory}{' '}
                    <span className="opacity-70 text-xs ml-1" data-id="element-838">
                      ({categoryPct}% of subtotal)
                    </span>
                  </span>
                  <span className="font-medium" data-id="element-839">
                    +₦{categoryAmount.toFixed(2)}
                  </span>
                </motion.div>}

              <div className="flex justify-between items-center text-sm" data-id="element-840">
                <span className="text-slate-300" data-id="element-841">
                  Service Fee{' '}
                  <span className="text-slate-500 text-xs ml-1" data-id="element-842">
                    ({serviceFeePct}% of ₦
                    {(subtotal + surgeAmount + categoryAmount).toFixed(2)})
                  </span>
                </span>
                <span className="font-medium" data-id="element-843">
                  ₦{serviceFeeAmount.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between items-center text-sm" data-id="element-844">
                <span className="text-slate-300 flex items-center gap-1" data-id="element-845">
                  FCT Dev Levy
                  <ShieldAlert size={12} className="text-slate-500" data-id="element-846" />
                </span>
                <span className="font-medium" data-id="element-847">₦{fctDevLevy.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 p-6 border-t border-slate-800 relative z-10" data-id="element-848">
            <div className="flex justify-between items-end" data-id="element-849">
              <div data-id="element-850">
                <p className="text-sm text-slate-400 mb-1" data-id="element-851">
                  Total Estimated Fare
                </p>
                <p className="text-xs text-slate-500" data-id="element-852">
                  For a {sampleDistance}km trip
                  {calculatorCategory !== 'none' ? ` • ${calculatorCategory}` : ''}
                </p>
              </div>
              <div className="text-3xl font-bold text-famo-gold" data-id="element-853">
                ₦
                {totalFare.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Item Category Charges Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.52
    }} className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-854">
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-blue-50/30" data-id="element-855">
          <div data-id="element-856">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2" data-id="element-857">
              <Package size={20} className="text-blue-500" data-id="element-858" />
              Item Category Charges
            </h3>
            <p className="text-sm text-slate-500 mt-1" data-id="element-859">
              Set percentage markups based on the type of item being delivered.
              Applied to the base fare + distance cost.
            </p>
          </div>
          <div className="text-sm text-slate-500 bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm" data-id="element-860">
            <span className="font-medium text-slate-700" data-id="element-861">
              {categoryCharges.filter(c => c.enabled).length}
            </span>{' '}
            of {categoryCharges.length} categories active
          </div>
        </div>

        <div className="p-6" data-id="element-862">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-863">
            {categoryCharges.map(cat => <div key={cat.id} data-id="element-864">
                {editingCategoryId === cat.id ? <div className="p-4 rounded-lg border-2 border-blue-300 bg-blue-50 space-y-3 h-full" data-id="element-865">
                    <div data-id="element-866">
                      <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-867">
                        Category Name
                      </label>
                      <input type="text" value={editCategory.name} onChange={e => setEditCategory({
                  ...editCategory,
                  name: e.target.value
                })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none" data-id="element-868" />
                    </div>
                    <div data-id="element-869">
                      <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-870">
                        Description
                      </label>
                      <input type="text" value={editCategory.description} onChange={e => setEditCategory({
                  ...editCategory,
                  description: e.target.value
                })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none" data-id="element-871" />
                    </div>
                    <div data-id="element-872">
                      <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-873">
                        Charge %
                      </label>
                      <div className="relative" data-id="element-874">
                        <input type="number" value={editCategory.percentage} onChange={e => setEditCategory({
                    ...editCategory,
                    percentage: Number(e.target.value)
                  })} className="w-full bg-white border border-slate-200 rounded-lg text-sm pl-3 pr-7 py-2 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none" data-id="element-875" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-876">
                          %
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end pt-1" data-id="element-877">
                      <button onClick={cancelEditCategory} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-md transition-colors" data-id="element-878">
                        Cancel
                      </button>
                      <button onClick={handleEditCategory} className="px-3 py-1.5 text-xs font-medium bg-blue-500 text-white hover:bg-blue-600 rounded-md transition-colors" data-id="element-879">
                        Save
                      </button>
                    </div>
                  </div> : <div className={`p-4 rounded-lg border transition-all h-full ${cat.enabled ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-50 border-slate-100'}`} data-id="element-880">
                    <div className="flex items-start justify-between mb-3" data-id="element-881">
                      <div className="flex items-center gap-2.5" data-id="element-882">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cat.enabled ? 'bg-blue-100 text-blue-600' : 'bg-slate-200 text-slate-400'}`} data-id="element-883">
                          {getCategoryIcon(cat.name)}
                        </div>
                        <div data-id="element-884">
                          <p className={`font-medium text-sm ${cat.enabled ? 'text-slate-800' : 'text-slate-400'}`} data-id="element-885">
                            {cat.name}
                          </p>
                          <p className={`text-xs ${cat.enabled ? 'text-slate-500' : 'text-slate-400'}`} data-id="element-886">
                            {cat.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 -mt-1 -mr-1" data-id="element-887">
                        <button onClick={() => startEditCategory(cat)} className="text-slate-400 hover:text-blue-500 transition-colors p-1" data-id="element-888">
                          <Pencil size={14} data-id="element-889" />
                        </button>
                        <button onClick={() => deleteCategory(cat.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1" data-id="element-890">
                          <Trash2 size={14} data-id="element-891" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100" data-id="element-892">
                      <span className={`px-2.5 py-1 rounded text-xs font-bold ${cat.enabled ? cat.percentage > 0 ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`} data-id="element-893">
                        {cat.percentage > 0 ? `+${cat.percentage}%` : 'No charge'}
                      </span>
                      <ToggleSwitch enabled={cat.enabled} onChange={() => toggleCategory(cat.id)} data-id="element-894" />
                    </div>
                  </div>}
              </div>)}

            {/* Add Category Card */}
            {showAddCategory ? <div className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50 space-y-3" data-id="element-895">
                <div data-id="element-896">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-897">
                    Category Name
                  </label>
                  <input type="text" value={newCategory.name} onChange={e => setNewCategory({
                ...newCategory,
                name: e.target.value
              })} placeholder="e.g. Medical Supplies" className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none" data-id="element-898" />
                </div>
                <div data-id="element-899">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-900">
                    Description
                  </label>
                  <input type="text" value={newCategory.description} onChange={e => setNewCategory({
                ...newCategory,
                description: e.target.value
              })} placeholder="e.g. Medicines, first aid, lab samples" className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none" data-id="element-901" />
                </div>
                <div data-id="element-902">
                  <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-903">
                    Charge %
                  </label>
                  <div className="relative" data-id="element-904">
                    <input type="number" value={newCategory.percentage || ''} onChange={e => setNewCategory({
                  ...newCategory,
                  percentage: Number(e.target.value)
                })} placeholder="0" className="w-full bg-white border border-slate-200 rounded-lg text-sm pl-3 pr-7 py-2 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none" data-id="element-905" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-906">
                      %
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-1" data-id="element-907">
                  <button onClick={() => setShowAddCategory(false)} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-md transition-colors" data-id="element-908">
                    Cancel
                  </button>
                  <button onClick={handleAddCategory} className="px-3 py-1.5 text-xs font-medium bg-blue-500 text-white hover:bg-blue-600 rounded-md transition-colors" data-id="element-909">
                    Add Category
                  </button>
                </div>
              </div> : <button onClick={() => setShowAddCategory(true)} className="py-4 border-2 border-dashed border-slate-200 rounded-lg text-sm font-medium text-slate-500 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50 transition-all flex flex-col items-center justify-center gap-2 min-h-[120px]" data-id="element-910">
                <Plus size={20} data-id="element-911" />
                Add Category
              </button>}
          </div>
        </div>
      </motion.div>

      {/* Surge Pricing Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.55
    }} className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-912">
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-amber-50/30" data-id="element-913">
          <div data-id="element-914">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2" data-id="element-915">
              <Zap size={20} className="text-amber-500" data-id="element-916" />
              Surge Pricing Controls
            </h3>
            <p className="text-sm text-slate-500 mt-1" data-id="element-917">
              Apply percentage markups to specific locations or during peak
              hours.
            </p>
          </div>
          <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm" data-id="element-918">
            <span className={`text-sm font-medium ${surgeEnabled ? 'text-amber-600' : 'text-slate-500'}`} data-id="element-919">
              {surgeEnabled ? 'Surge System Active' : 'Surge System Disabled'}
            </span>
            <ToggleSwitch enabled={surgeEnabled} onChange={() => {
            setSurgeEnabled(!surgeEnabled);
            if (surgeEnabled) setCalculatorSurgeEnabled(false);
          }} data-id="element-920" />
          </div>
        </div>

        <div className={`p-6 transition-opacity duration-300 ${!surgeEnabled ? 'opacity-50 pointer-events-none grayscale-[0.5]' : ''}`} data-id="element-921">
          {/* Live Map Panel */}
          <div className="mb-8 rounded-xl overflow-hidden border border-slate-200 shadow-sm relative h-72 z-0" data-id="element-922">
            <MapContainer center={[6.5, 3.4]} zoom={11} scrollWheelZoom={false} className="h-full w-full" data-id="element-923">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-924" />
              {locationSurges.map(loc => <Circle key={loc.id} center={[loc.lat, loc.lng]} radius={loc.radius} pathOptions={{
              color: loc.enabled ? '#F59E0B' : '#94A3B8',
              fillColor: loc.enabled ? '#F59E0B' : '#94A3B8',
              fillOpacity: loc.enabled ? 0.25 : 0.1,
              weight: loc.enabled ? 2 : 1
            }} data-id="element-925">
                  <Tooltip sticky data-id="element-926">
                    <div className="font-medium" data-id="element-927">{loc.name}</div>
                    <div className={loc.enabled ? 'text-amber-600 font-bold' : 'text-slate-500'} data-id="element-928">
                      {loc.enabled ? `+${loc.percentage}% Surge` : 'Disabled'}
                    </div>
                  </Tooltip>
                  <Popup data-id="element-929">
                    <div className="text-sm" data-id="element-930">
                      <p className="font-bold mb-1" data-id="element-931">{loc.name}</p>
                      <p className="text-slate-600 mb-2" data-id="element-932">
                        Surge: +{loc.percentage}%
                      </p>
                      <button onClick={() => toggleLocationSurge(loc.id)} className={`px-3 py-1 rounded text-xs font-medium w-full ${loc.enabled ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`} data-id="element-933">
                        {loc.enabled ? 'Disable Zone' : 'Enable Zone'}
                      </button>
                    </div>
                  </Popup>
                </Circle>)}
            </MapContainer>

            {/* Map Legend */}
            <div className="absolute bottom-3 left-3 z-[400] bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm border border-slate-200 text-xs" data-id="element-934">
              <p className="font-bold text-slate-700 mb-1.5" data-id="element-935">Surge Zones</p>
              <div className="flex items-center gap-2 mb-1" data-id="element-936">
                <div className="w-3 h-3 rounded-full bg-amber-500/30 border border-amber-500" data-id="element-937"></div>
                <span className="text-slate-600" data-id="element-938">Active</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-939">
                <div className="w-3 h-3 rounded-full bg-slate-400/20 border border-slate-400" data-id="element-940"></div>
                <span className="text-slate-600" data-id="element-941">Inactive</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-942">
            {/* Location Surge Zones */}
            <div className="space-y-4" data-id="element-943">
              <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-944">
                <MapPin size={18} className="text-slate-400" data-id="element-945" />
                Location Surge Zones
              </h4>
              <div className="space-y-3" data-id="element-946">
                {locationSurges.map(loc => <div key={loc.id} data-id="element-947">
                    {editingLocationId === loc.id ? <div className="p-4 rounded-lg border-2 border-amber-300 bg-amber-50 space-y-3" data-id="element-948">
                        <div className="grid grid-cols-3 gap-3" data-id="element-949">
                          <div className="col-span-2" data-id="element-950">
                            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-951">
                              Location Name
                            </label>
                            <input type="text" value={editLocation.name} onChange={e => setEditLocation({
                        ...editLocation,
                        name: e.target.value
                      })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-952" />
                          </div>
                          <div data-id="element-953">
                            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-954">
                              Surge %
                            </label>
                            <div className="relative" data-id="element-955">
                              <input type="number" value={editLocation.percentage} onChange={e => setEditLocation({
                          ...editLocation,
                          percentage: Number(e.target.value)
                        })} className="w-full bg-white border border-slate-200 rounded-lg text-sm pl-3 pr-7 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-956" />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-957">
                                %
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3" data-id="element-958">
                          <div data-id="element-959">
                            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-960">
                              Latitude
                            </label>
                            <input type="number" step="0.0001" value={editLocation.lat} onChange={e => setEditLocation({
                        ...editLocation,
                        lat: Number(e.target.value)
                      })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-961" />
                          </div>
                          <div data-id="element-962">
                            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-963">
                              Longitude
                            </label>
                            <input type="number" step="0.0001" value={editLocation.lng} onChange={e => setEditLocation({
                        ...editLocation,
                        lng: Number(e.target.value)
                      })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-964" />
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end pt-1" data-id="element-965">
                          <button onClick={cancelEditLocation} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-md transition-colors" data-id="element-966">
                            Cancel
                          </button>
                          <button onClick={handleEditLocation} className="px-3 py-1.5 text-xs font-medium bg-amber-500 text-white hover:bg-amber-600 rounded-md transition-colors" data-id="element-967">
                            Save
                          </button>
                        </div>
                      </div> : <div className={`flex items-center justify-between p-3 rounded-lg border ${loc.enabled ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-50 border-slate-100'}`} data-id="element-968">
                        <div className="flex items-center gap-3" data-id="element-969">
                          <ToggleSwitch enabled={loc.enabled} onChange={() => toggleLocationSurge(loc.id)} data-id="element-970" />
                          <span className={`font-medium ${loc.enabled ? 'text-slate-800' : 'text-slate-400'}`} data-id="element-971">
                            {loc.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-972">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${loc.enabled ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500'}`} data-id="element-973">
                            +{loc.percentage}%
                          </span>
                          <button onClick={() => startEditLocation(loc)} className="text-slate-400 hover:text-amber-500 transition-colors p-1" data-id="element-974">
                            <Pencil size={14} data-id="element-975" />
                          </button>
                          <button onClick={() => deleteLocationSurge(loc.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1" data-id="element-976">
                            <Trash2 size={16} data-id="element-977" />
                          </button>
                        </div>
                      </div>}
                  </div>)}
                {showAddLocation ? <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 space-y-3" data-id="element-978">
                    <div className="grid grid-cols-3 gap-3" data-id="element-979">
                      <div className="col-span-2" data-id="element-980">
                        <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-981">
                          Location Name
                        </label>
                        <input type="text" value={newLocation.name} onChange={e => setNewLocation({
                      ...newLocation,
                      name: e.target.value
                    })} placeholder="e.g. Victoria Island" className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-982" />
                      </div>
                      <div data-id="element-983">
                        <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-984">
                          Surge %
                        </label>
                        <div className="relative" data-id="element-985">
                          <input type="number" value={newLocation.percentage || ''} onChange={e => setNewLocation({
                        ...newLocation,
                        percentage: Number(e.target.value)
                      })} placeholder="0" className="w-full bg-white border border-slate-200 rounded-lg text-sm pl-3 pr-7 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-986" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-987">
                            %
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-988">
                      <div data-id="element-989">
                        <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-990">
                          Latitude
                        </label>
                        <input type="number" step="0.0001" value={newLocation.lat || ''} onChange={e => setNewLocation({
                      ...newLocation,
                      lat: Number(e.target.value)
                    })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-991" />
                      </div>
                      <div data-id="element-992">
                        <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-993">
                          Longitude
                        </label>
                        <input type="number" step="0.0001" value={newLocation.lng || ''} onChange={e => setNewLocation({
                      ...newLocation,
                      lng: Number(e.target.value)
                    })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-994" />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end pt-1" data-id="element-995">
                      <button onClick={() => setShowAddLocation(false)} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-md transition-colors" data-id="element-996">
                        Cancel
                      </button>
                      <button onClick={handleAddLocation} className="px-3 py-1.5 text-xs font-medium bg-amber-500 text-white hover:bg-amber-600 rounded-md transition-colors" data-id="element-997">
                        Add Zone
                      </button>
                    </div>
                  </div> : <button onClick={() => setShowAddLocation(true)} className="w-full py-3 border-2 border-dashed border-slate-200 rounded-lg text-sm font-medium text-slate-500 hover:text-amber-600 hover:border-amber-300 hover:bg-amber-50 transition-all flex items-center justify-center gap-2" data-id="element-998">
                    <Plus size={16} data-id="element-999" /> Add Location Zone
                  </button>}
              </div>
            </div>

            {/* Peak Hour Surge */}
            <div className="space-y-4" data-id="element-1000">
              <div className="flex items-center justify-between" data-id="element-1001">
                <h4 className="font-bold text-slate-700 flex items-center gap-2" data-id="element-1002">
                  <Clock size={18} className="text-slate-400" data-id="element-1003" />
                  Peak Hour Surge
                </h4>
              </div>

              {/* Live Status Indicator */}
              <div className={`p-3 rounded-lg border flex items-center gap-3 ${activePeak ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`} data-id="element-1004">
                <div className="relative flex h-3 w-3" data-id="element-1005">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${activePeak ? 'bg-amber-400' : 'bg-green-400'}`} data-id="element-1006"></span>
                  <span className={`relative inline-flex rounded-full h-3 w-3 ${activePeak ? 'bg-amber-500' : 'bg-green-500'}`} data-id="element-1007"></span>
                </div>
                <div className="flex-1" data-id="element-1008">
                  <p className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-1009">
                    Current Status (
                    {currentTime.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    )
                  </p>
                  {activePeak ? <p className="text-sm font-bold text-amber-700" data-id="element-1010">
                      SURGE ACTIVE — {activePeak.label} (+
                      {activePeak.percentage}%)
                    </p> : <p className="text-sm font-bold text-green-700" data-id="element-1011">
                      Normal Pricing
                    </p>}
                </div>
              </div>

              <div className="space-y-3" data-id="element-1012">
                {peakHourSurges.map(peak => <div key={peak.id} data-id="element-1013">
                    {editingPeakHourId === peak.id ? <div className="p-4 rounded-lg border-2 border-amber-300 bg-amber-50 space-y-3" data-id="element-1014">
                        <div className="grid grid-cols-2 gap-3" data-id="element-1015">
                          <div data-id="element-1016">
                            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1017">
                              Start Time
                            </label>
                            <input type="time" value={editPeakHour.startTime} onChange={e => setEditPeakHour({
                        ...editPeakHour,
                        startTime: e.target.value
                      })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-1018" />
                          </div>
                          <div data-id="element-1019">
                            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1020">
                              End Time
                            </label>
                            <input type="time" value={editPeakHour.endTime} onChange={e => setEditPeakHour({
                        ...editPeakHour,
                        endTime: e.target.value
                      })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-1021" />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3" data-id="element-1022">
                          <div className="col-span-2" data-id="element-1023">
                            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1024">
                              Label
                            </label>
                            <input type="text" value={editPeakHour.label} onChange={e => setEditPeakHour({
                        ...editPeakHour,
                        label: e.target.value
                      })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-1025" />
                          </div>
                          <div data-id="element-1026">
                            <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1027">
                              Surge %
                            </label>
                            <div className="relative" data-id="element-1028">
                              <input type="number" value={editPeakHour.percentage} onChange={e => setEditPeakHour({
                          ...editPeakHour,
                          percentage: Number(e.target.value)
                        })} className="w-full bg-white border border-slate-200 rounded-lg text-sm pl-3 pr-7 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-1029" />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-1030">
                                %
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end pt-1" data-id="element-1031">
                          <button onClick={cancelEditPeakHour} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-md transition-colors" data-id="element-1032">
                            Cancel
                          </button>
                          <button onClick={handleEditPeakHour} className="px-3 py-1.5 text-xs font-medium bg-amber-500 text-white hover:bg-amber-600 rounded-md transition-colors" data-id="element-1033">
                            Save
                          </button>
                        </div>
                      </div> : <div className={`flex items-center justify-between p-3 rounded-lg border ${peak.enabled ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-50 border-slate-100'}`} data-id="element-1034">
                        <div className="flex items-center gap-3" data-id="element-1035">
                          <ToggleSwitch enabled={peak.enabled} onChange={() => togglePeakHourSurge(peak.id)} data-id="element-1036" />
                          <div data-id="element-1037">
                            <p className={`font-medium text-sm ${peak.enabled ? 'text-slate-800' : 'text-slate-400'}`} data-id="element-1038">
                              {peak.startTime} - {peak.endTime}
                            </p>
                            <p className={`text-xs ${peak.enabled ? 'text-slate-500' : 'text-slate-400'}`} data-id="element-1039">
                              {peak.label}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1040">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${peak.enabled ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500'}`} data-id="element-1041">
                            +{peak.percentage}%
                          </span>
                          <button onClick={() => startEditPeakHour(peak)} className="text-slate-400 hover:text-amber-500 transition-colors p-1" data-id="element-1042">
                            <Pencil size={14} data-id="element-1043" />
                          </button>
                          <button onClick={() => deletePeakHourSurge(peak.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1" data-id="element-1044">
                            <Trash2 size={16} data-id="element-1045" />
                          </button>
                        </div>
                      </div>}
                  </div>)}
                {showAddPeakHour ? <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 space-y-3" data-id="element-1046">
                    <div className="grid grid-cols-2 gap-3" data-id="element-1047">
                      <div data-id="element-1048">
                        <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1049">
                          Start Time
                        </label>
                        <input type="time" value={newPeakHour.startTime} onChange={e => setNewPeakHour({
                      ...newPeakHour,
                      startTime: e.target.value
                    })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-1050" />
                      </div>
                      <div data-id="element-1051">
                        <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1052">
                          End Time
                        </label>
                        <input type="time" value={newPeakHour.endTime} onChange={e => setNewPeakHour({
                      ...newPeakHour,
                      endTime: e.target.value
                    })} className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-1053" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3" data-id="element-1054">
                      <div className="col-span-2" data-id="element-1055">
                        <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1056">
                          Label
                        </label>
                        <input type="text" value={newPeakHour.label} onChange={e => setNewPeakHour({
                      ...newPeakHour,
                      label: e.target.value
                    })} placeholder="e.g. Morning Rush" className="w-full bg-white border border-slate-200 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-1057" />
                      </div>
                      <div data-id="element-1058">
                        <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-1059">
                          Surge %
                        </label>
                        <div className="relative" data-id="element-1060">
                          <input type="number" value={newPeakHour.percentage || ''} onChange={e => setNewPeakHour({
                        ...newPeakHour,
                        percentage: Number(e.target.value)
                      })} placeholder="0" className="w-full bg-white border border-slate-200 rounded-lg text-sm pl-3 pr-7 py-2 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 outline-none" data-id="element-1061" />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-1062">
                            %
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end pt-1" data-id="element-1063">
                      <button onClick={() => setShowAddPeakHour(false)} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-md transition-colors" data-id="element-1064">
                        Cancel
                      </button>
                      <button onClick={handleAddPeakHour} className="px-3 py-1.5 text-xs font-medium bg-amber-500 text-white hover:bg-amber-600 rounded-md transition-colors" data-id="element-1065">
                        Add Time
                      </button>
                    </div>
                  </div> : <button onClick={() => setShowAddPeakHour(true)} className="w-full py-3 border-2 border-dashed border-slate-200 rounded-lg text-sm font-medium text-slate-500 hover:text-amber-600 hover:border-amber-300 hover:bg-amber-50 transition-all flex items-center justify-center gap-2" data-id="element-1066">
                    <Plus size={16} data-id="element-1067" /> Add Peak Hour
                  </button>}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Change History */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.6
    }} className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-1068">
        <div className="p-6 border-b border-slate-100 flex items-center gap-2" data-id="element-1069">
          <History size={20} className="text-slate-500" data-id="element-1070" />
          <h3 className="text-lg font-bold text-slate-800" data-id="element-1071">
            Pricing Change History
          </h3>
        </div>
        <DataTable data={mockHistory} columns={historyColumns} searchPlaceholder="Search history..." data-id="element-1072" />
      </motion.div>
    </div>;
}